import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";

const ToastContext = createContext(null);

const ENTER_MS_DEFAULT = 500;  // 0.5초 (등장)
const HOLD_MS_DEFAULT = 1000;  // 1초   (완전히 보이는 상태 유지)
const EXIT_MS_DEFAULT = 200;   // 0.2초 (퇴장)

export function ToastProvider({ children }) {
  const [toast, setToast] = useState({
    visible: false,
    open: false,
    message: "",
    animMs: ENTER_MS_DEFAULT,
    placement: "bottom",     // "top" | "bottom"
    topOffset: "450px",       // px
    bottomOffset: "88px",
  });

  const stayTimerRef = useRef(null);
  const exitTimerRef = useRef(null);

  const showToast = useCallback((message, options = {}) => {
    const enterMs = options.enterMs ?? ENTER_MS_DEFAULT;
    const holdMs = options.holdMs ?? HOLD_MS_DEFAULT;
    const exitMs = options.exitMs ?? EXIT_MS_DEFAULT;

    const placement = options.placement ?? "bottom";
    const topOffset = options.topOffset ?? "450px";
    const bottomOffset = options.bottomOffset ?? "88px";

    // 기존 타이머 정리
    if (stayTimerRef.current) clearTimeout(stayTimerRef.current);
    if (exitTimerRef.current) clearTimeout(exitTimerRef.current);

    // 1) 탭 즉시: 딜레이 없이 바로 나타나기 시작 (opacity 0 → 100, 0.5초)
    setToast({
      visible: true,
      open: true,
      message,
      animMs: enterMs,
      placement,
      topOffset,
      bottomOffset,
    });

    // 2) (등장 시간 0.5초 + 유지 시간 1초) 지난 뒤 퇴장 시작
    stayTimerRef.current = setTimeout(() => {
      // 퇴장: opacity 100 → 0, 0.2초
      setToast((prev) => ({
        ...prev,
        open: false,
        animMs: exitMs,
      }));

      // 3) 퇴장 애니메이션 끝난 후 DOM에서 제거
      exitTimerRef.current = setTimeout(() => {
        setToast((prev) => ({
          ...prev,
          visible: false,
          message: "",
        }));
      }, exitMs);
    }, enterMs + holdMs);
  }, []);

  const hideToast = useCallback(() => {
    // 외부에서 강제로 닫을 때 (예: 버튼 클릭)
    if (stayTimerRef.current) clearTimeout(stayTimerRef.current);
    if (exitTimerRef.current) clearTimeout(exitTimerRef.current);

    const exitMs = EXIT_MS_DEFAULT;

    setToast((prev) => ({
      ...prev,
      open: false,
      animMs: exitMs,
    }));

    exitTimerRef.current = setTimeout(() => {
      setToast((prev) => ({
        ...prev,
        visible: false,
        message: "",
      }));
    }, exitMs);
  }, []);

  const positionStyle =
    toast.placement === "bottom"
      ? { bottom: toast.bottomOffset }
      : { top: toast.topOffset };


  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}

      {/* visible일 때만 DOM 렌더 */}
      {toast.visible && (
        <div
          className="
            w-full
            fixed
            left-1/2 -translate-x-1/2
            z-[9999]
            px-[20px]
          "
          style={positionStyle}
        >
          <div
            className={`
              flex items-center justify-center
              h-[38px] rounded-[12px] py-[10px] px-[16px]
              text-text-white-absolute text-c2
              bg-surface-dark-absolute-dimmed_80
              transition-all ease-out
              ${toast.open
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-2"
              }
            `}
            style={{
              // 등장 시에는 0.5초, 퇴장 시에는 0.2초가 각각 들어감
              transitionDuration: `${toast.animMs}ms`,
            }}
          >
            {toast.message}
          </div>
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast는 <ToastProvider> 안에서만 사용할 수 있습니다.");
  }
  return ctx;
}